<template>
    <div>
        <p class="text-muted small mb-3">
            قرارداد همکاری تجاری (همکاری در فروش) شما پس از امضای الکترونیکی در این بخش نگهداری می‌شود
            و همیشه قابل مشاهده و دریافت است.
        </p>

        <div v-if="loading && !contract" class="text-center py-5">
            <span class="spinner-border text-success"></span>
        </div>

        <div v-else-if="error" class="alert alert-danger d-flex align-items-center justify-content-between">
            <span>{{ error }}</span>
            <button type="button" class="btn btn-sm btn-outline-danger rounded-pill" @click="load">تلاش مجدد</button>
        </div>

        <div v-else-if="!contract" class="text-center text-muted py-5">
            <i class="fa fa-file-contract fa-3x mb-3 d-block"></i>
            قرارداد همکاری فروشی برای شما ثبت نشده است.
        </div>

        <div v-else class="contract-card border rounded p-3 mb-3">
            <div class="d-flex flex-wrap align-items-start mb-2">
                <div class="ml-auto mb-2">
                    <h6 class="mb-1 text-white-in-dark-mode">قرارداد همکاری تجاری</h6>
                    <small class="text-muted">{{ partnerName }}</small>
                </div>
                <span class="badge badge-pill badge-success p-2">امضا شده</span>
            </div>

            <div class="row small">
                <div v-for="row in contractRows" :key="row.label" class="col-6 col-md-3 mb-2">
                    <div class="text-muted">{{ row.label }}</div>
                    <div class="en_num text-white-in-dark-mode" :dir="row.ltr ? 'ltr' : null">{{ row.value || '—' }}</div>
                </div>
            </div>

            <h6 class="section-title">{{ isLegal ? 'همکار تجاری (شخص حقوقی)' : 'همکار تجاری (شخص حقیقی)' }}</h6>
            <div class="row small">
                <div v-for="row in partnerRows" :key="row.label" class="col-12 col-md-6 mb-2 d-flex">
                    <span class="text-muted ml-2 text-nowrap">{{ row.label }}:</span>
                    <span class="text-white-in-dark-mode en_num" :dir="row.ltr ? 'ltr' : null">{{ row.value || '—' }}</span>
                </div>
            </div>

            <div class="d-flex flex-wrap justify-content-end mt-1">
                <button type="button" class="btn btn-sm btn-light rounded-pill px-3 mr-2 mb-1" :disabled="busy" @click="viewPdf">
                    <i class="fa fa-eye ml-1"></i>مشاهده
                </button>
                <button type="button" class="btn btn-sm btn-outline-success rounded-pill px-3 mr-2 mb-1" :disabled="busy" @click="download">
                    <i class="fa fa-download ml-1"></i>دانلود
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { marketingService } from '@/common/api.service'

export default {
    name: 'MarketingContract',
    data() {
        return {
            loading: false,
            error: '',
            contract: null,
            busy: false,
        }
    },
    computed: {
        snapshot() {
            return (this.contract && this.contract.data) || {}
        },
        isLegal() {
            return this.snapshot.applicant_type === 'legal'
        },
        partnerName() {
            const a = this.snapshot.applicant || {}
            return this.isLegal ? a.company_name : a.full_name
        },
        contractRows() {
            const c = this.contract
            return [
                { label: 'شماره قرارداد', value: c.contract_number, ltr: true },
                { label: 'تاریخ امضا', value: c.signed_at_fa },
                { label: 'کد رهگیری', value: c.tracking_code, ltr: true },
                { label: 'شماره همراه امضاکننده', value: c.signer_mobile, ltr: true },
            ]
        },
        partnerRows() {
            const a = this.snapshot.applicant || {}
            const r = this.snapshot.representative || {}
            const rows = this.isLegal
                ? [
                    { label: 'نام شرکت', value: a.company_name },
                    { label: 'شناسه ملی', value: a.national_legal_code },
                    { label: 'شماره ثبت', value: a.register_number },
                    { label: 'نماینده مجاز', value: r.full_name },
                    { label: 'کد ملی نماینده', value: r.national_code },
                ]
                : [
                    { label: 'نام و نام خانوادگی', value: a.full_name },
                    { label: 'کد ملی', value: a.national_code },
                ]
            return rows.concat([
                { label: 'شماره همراه', value: a.mobile, ltr: true },
                { label: 'تلفن ثابت', value: a.telephone, ltr: true },
                { label: 'ایمیل', value: a.email, ltr: true },
                { label: 'کد پستی', value: a.postal_code },
                { label: 'نشانی', value: a.address },
            ])
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        load() {
            this.loading = true
            this.error = ''
            marketingService.fetchContract()
                .then(({ data }) => { this.contract = data.data.contract })
                .catch(({ response }) => {
                    this.error = (response && response.data && response.data.message) || 'دریافت قرارداد همکاری فروش ممکن نشد.'
                })
                .finally(() => { this.loading = false })
        },
        viewPdf() {
            // Open the tab synchronously so popup blockers allow it, then point it at the blob.
            const tab = window.open('', '_blank')
            this.fetchPdf(false, url => {
                if (tab) {
                    tab.location.href = url
                } else {
                    window.location.href = url
                }
            }, () => tab && tab.close())
        },
        download() {
            this.fetchPdf(true, url => {
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `paystar-sales-partnership-contract-${this.contract.contract_number}.pdf`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            })
        },
        fetchPdf(download, done, failed = () => {}) {
            this.busy = true
            marketingService.contractPdf(download)
                .then(({ data }) => {
                    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
                    done(url)
                    setTimeout(() => window.URL.revokeObjectURL(url), 60000)
                })
                .catch(() => {
                    failed()
                    this.toastr.error('دریافت فایل قرارداد ممکن نشد. لطفاً دوباره تلاش کنید.')
                })
                .finally(() => { this.busy = false })
        },
    },
}
</script>

<style scoped>
.contract-card {
    transition: box-shadow .15s;
}

.contract-card:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, .06);
}

.section-title {
    color: #00bea5;
    font-size: .95rem;
    margin: 1rem 0 .75rem;
    padding-bottom: .4rem;
    border-bottom: 1px solid rgba(0, 0, 0, .08);
}
</style>
